import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';

const HorizontalNavbar = ({ isOpen, setIsOpen }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [generatedLink, setGeneratedLink] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    
    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const generateInviteLink = async () => {
        try {
            const response = await api.post('/admin/generate-invite');
            if (response.data && response.data.token) {
                const inviteLink = `${window.location.origin}/admin/invite-simple/${response.data.token}`;
                setGeneratedLink(inviteLink);
                setIsInviteModalOpen(true);
            } else {
                toast.error('Failed to generate invite token');
            }
        } catch (error) {
            console.error('Error generating invite link:', error);
            toast.error(error.response?.data?.message || 'Failed to generate invite link');
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedLink);
        setIsCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setIsCopied(false), 2000);
    };

    // Global search function
    const performSearch = async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        setIsLoading(true);
        try {
            const results = [];
            
            // Search in users
            try {
                const usersResponse = await api.get(`/users?search=${encodeURIComponent(query)}`);
                const users = Array.isArray(usersResponse.data) ? usersResponse.data : [];
                users.slice(0, 3).forEach(user => {
                    if (user.name.toLowerCase().includes(query.toLowerCase()) || 
                        user.email.toLowerCase().includes(query.toLowerCase())) {
                        results.push({
                            type: 'user',
                            title: user.name,
                            subtitle: user.email,
                            action: () => navigate('/users'),
                            icon: '👤'
                        });
                    }
                });
            } catch (err) {
                console.log('Error searching users:', err);
            }

            // Search in tasks
            try {
                const tasksResponse = await api.get('/tasks');
                const tasks = Array.isArray(tasksResponse.data) ? tasksResponse.data : [];
                tasks.filter(task => 
                    task.title.toLowerCase().includes(query.toLowerCase()) ||
                    task.description.toLowerCase().includes(query.toLowerCase())
                ).slice(0, 3).forEach(task => {
                    results.push({
                        type: 'task',
                        title: task.title,
                        subtitle: task.description,
                        action: () => navigate('/tasks'),
                        icon: '📝'
                    });
                });
            } catch (err) {
                console.log('Error searching tasks:', err);
            }

            // Search in posts
            try {
                const postsResponse = await api.get('/posts');
                const posts = Array.isArray(postsResponse.data) ? postsResponse.data : [];
                posts.filter(post => 
                    post.title.toLowerCase().includes(query.toLowerCase()) ||
                    (post.content && post.content.toLowerCase().includes(query.toLowerCase()))
                ).slice(0, 3).forEach(post => {
                    results.push({
                        type: 'post',
                        title: post.title,
                        subtitle: post.excerpt || 'Blog post',
                        action: () => navigate('/posts'),
                        icon: '📄'
                    });
                });
            } catch (err) {
                console.log('Error searching posts:', err);
            }

            // Search in learning paths
            try {
                const pathsResponse = await api.get('/learning-paths');
                if (pathsResponse.data.success) {
                    const paths = Array.isArray(pathsResponse.data.data) ? pathsResponse.data.data : [];
                    paths.filter(path => 
                        path.title.toLowerCase().includes(query.toLowerCase()) ||
                        path.description.toLowerCase().includes(query.toLowerCase())
                    ).slice(0, 3).forEach(path => {
                        results.push({
                            type: 'learning-path',
                            title: path.title,
                            subtitle: path.description,
                            action: () => navigate('/learning-paths'),
                            icon: '🎯'
                        });
                    });
                }
            } catch (err) {
                console.log('Error searching learning paths:', err);
            }

            // Add quick actions
            const actions = [
                { name: 'Create Task', keywords: ['create task', 'new task', 'add task'], action: () => navigate('/task/new'), icon: '➕', subtitle: 'Create a new task' },
                { name: 'Create Post', keywords: ['create post', 'new post', 'add post', 'write post'], action: () => navigate('/post/new'), icon: '✍️', subtitle: 'Write a new blog post' },
                { name: 'Create Learning Path', keywords: ['create learning path', 'new learning path', 'add learning path'], action: () => navigate('/learning-paths/create'), icon: '🚀', subtitle: 'Create a new learning path' },
                { name: 'Invite Admin', keywords: ['invite admin', 'add admin', 'new admin'], action: generateInviteLink, icon: '👤', subtitle: 'Generate admin invite link' }
            ];

            // Check if query matches any action keywords
            actions.forEach(action => {
                const matchesKeyword = action.keywords.some(keyword => 
                    keyword.toLowerCase().includes(query.toLowerCase()) ||
                    query.toLowerCase().includes(keyword.toLowerCase())
                );
                
                if (matchesKeyword || action.name.toLowerCase().includes(query.toLowerCase())) {
                    results.push({
                        type: 'action',
                        title: action.name,
                        subtitle: action.subtitle,
                        action: action.action,
                        icon: action.icon
                    });
                }
            });

            // Add quick navigation options
            const pages = [
                { name: 'Dashboard', path: '/dashboard', icon: '🏠' },
                { name: 'Users', path: '/users', icon: '👥' },
                { name: 'Tasks', path: '/tasks', icon: '📝' },
                { name: 'Posts', path: '/posts', icon: '📄' },
                { name: 'Learning Paths', path: '/learning-paths', icon: '🎯' },
                { name: 'Admins', path: '/admins', icon: '⚙️' }
            ];

            pages.filter(page => 
                page.name.toLowerCase().includes(query.toLowerCase())
            ).forEach(page => {
                results.push({
                    type: 'page',
                    title: page.name,
                    subtitle: 'Navigate to page',
                    action: () => navigate(page.path),
                    icon: page.icon
                });
            });

            setSearchResults(results);
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Debounce search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchQuery) {
                performSearch(searchQuery);
            } else {
                setSearchResults([]);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const handleSearchFocus = () => {
        setIsSearchOpen(true);
    };

    const handleSearchBlur = () => {
        // Delay to allow clicking on results
        setTimeout(() => setIsSearchOpen(false), 200);
    };

    const handleResultClick = (result) => {
        result.action();
        setSearchQuery('');
        setSearchResults([]);
        setIsSearchOpen(false);
    };
    
    return (
        <div className={`relative h-16 flex justify-between lg:justify-center items-center px-4 lg:px-6 transition-all duration-300`}>
            {/* Mobile Menu Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
            >
                <img 
                    src="/favicon.svg" 
                    alt="UI Quarter Logo" 
                    className="w-8 h-8"
                />
            </button>

            {/* Centered Search Section */}
            <div className="flex-1 max-w-xs md:max-w-md mx-auto relative lg:max-w-md">
                <form onSubmit={(e) => e.preventDefault()} className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                        <svg className="h-5 w-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={handleSearchFocus}
                        onBlur={handleSearchBlur}
                        className="block w-full pl-12 pr-4 py-3 border border-white/20 rounded-2xl bg-white/10 backdrop-blur-xl backdrop-saturate-150 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#FF3366]/50 focus:border-white/30 focus:bg-white/15 transition-all duration-300 text-center shadow-lg"
                        placeholder="Search or type actions..."
                        style={{
                            backdropFilter: 'blur(20px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                        }}
                    />
                    
                    {/* Search Results Dropdown */}
                    {isSearchOpen && (searchResults.length > 0 || isLoading) && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-[#151515] border border-[#FF3366]/20 rounded-xl shadow-2xl py-2 max-h-96 overflow-y-auto z-50">
                            {isLoading ? (
                                <div className="px-4 py-3 text-gray-400 text-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#FF3366] mx-auto"></div>
                                    <span className="ml-2">Searching...</span>
                                </div>
                            ) : (
                                searchResults.map((result, index) => (
                                    <div
                                        key={index}
                                        onClick={() => handleResultClick(result)}
                                        className="px-4 py-3 hover:bg-[#FF3366]/10 cursor-pointer transition-colors border-b border-[#FF3366]/5 last:border-b-0"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-lg">{result.icon}</span>
                                            <div className="flex-1">
                                                <div className="text-white font-medium">{result.title}</div>
                                                <div className="text-gray-400 text-sm truncate">{result.subtitle}</div>
                                            </div>
                                            <div className="text-xs text-gray-500 capitalize px-2 py-1 bg-[#FF3366]/10 rounded">
                                                {result.type}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                            
                            {!isLoading && searchResults.length === 0 && searchQuery && (
                                <div className="px-4 py-6 text-gray-400 text-center">
                                    <div className="text-2xl mb-2">🔍</div>
                                    <div>No results found for "{searchQuery}"</div>
                                </div>
                            )}
                        </div>
                    )}
                </form>
            </div>

            {/* User info and actions */}
            <div className="flex items-center gap-2 lg:gap-4 ml-2 lg:ml-6">
                <div className="hidden md:block text-sm text-white text-right">
                    <p className="font-medium text-xs lg:text-sm">{user?.name || 'Admin User'}</p>
                    <p className="text-xs text-gray-400">{user?.role || 'admin'}</p>
                </div>
                
                <div className="relative group">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full cursor-pointer border-2 border-[#FF3366]/30 hover:border-[#FF3366] transition-all bg-gradient-to-br from-[#FF3366]/10 to-[#FF6B4D]/10 backdrop-blur-sm flex items-center justify-center">
                        <span className="text-xl lg:text-3xl">
                            {user?.role === 'superadmin' ? '👑' : '🛡️'}
                        </span>
                    </div>
                    
                    {/* Dropdown menu */}
                    <div className="absolute right-0 top-full mt-2 w-48 bg-[#151515] border border-[#FF3366]/20 rounded-lg shadow-lg py-1 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200">
                        <div className="px-4 py-2 border-b border-[#FF3366]/10">
                            <p className="text-white font-medium truncate">{user?.email || 'admin@example.com'}</p>
                        </div>
                        <button 
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2 text-gray-300 hover:bg-[#FF3366]/10 hover:text-white transition-colors flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Logout
                        </button>
                    </div>
                </div>
                
                <button
                    onClick={handleLogout}
                    className="hidden lg:flex items-center justify-center rounded-lg transition-all duration-300 
                    bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white 
                    hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90
                    shadow-[0_4px_12px_rgba(255,51,102,0.3)]
                    transform hover:translate-y-[-2px] px-4 py-2 gap-2"
                >
                    <svg 
                        className="w-5 h-5 flex-shrink-0" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                        />
                    </svg>
                    <span className="font-semibold text-sm">Logout</span>
                </button>
            </div>
            
            {/* Invite Modal - Birebir Admins.js'dekinin aynısı */}
            {isInviteModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl p-8 max-w-md w-full mx-4 border border-[#FF3366]/20 shadow-2xl">
                        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] mb-4">
                            Admin Invitation Link
                        </h2>
                        <p className="text-gray-300 mb-6">
                            Share this link with the person you want to invite as an admin:
                        </p>
                        <div className="flex items-center mb-6 bg-[#0A0A0A] rounded-lg border border-[#FF3366]/20">
                            <input
                                type="text"
                                value={generatedLink}
                                readOnly
                                className="flex-1 p-3 bg-transparent border-0 text-white focus:outline-none"
                            />
                            <button
                                onClick={copyToClipboard}
                                className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white px-4 py-3 rounded-r-lg font-medium hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200"
                            >
                                {isCopied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsInviteModalOpen(false)}
                                className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 transition-all duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HorizontalNavbar; 